import React, { useState, useMemo } from 'react';

interface AutocompleteOption {
  id: string;
  name: string;
  address: string;
  phone: string;
}

interface FormAutocompleteProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: AutocompleteOption[];
  placeholder?: string;
  required?: boolean;
}

export default function FormAutocomplete({
  id,
  label,
  value,
  onChange,
  options,
  placeholder,
  required = false
}: FormAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const filteredOptions = useMemo(() => {
    if (!inputValue) return options.slice(0, 5);

    return options.filter(option =>
      option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
      option.address.toLowerCase().includes(inputValue.toLowerCase())
    ).slice(0, 5);
  }, [inputValue, options]);

  const handleInputChange = (value: string) => {
    setInputValue(value);
    onChange(value);
    setShowSuggestions(true);
  };

  const handleSuggestionClick = (option: AutocompleteOption) => {
    const formattedValue = `${option.name} - ${option.address} - ${option.phone}`;
    setInputValue(formattedValue);
    onChange(formattedValue);
    setShowSuggestions(false);
  };

  return (
    <div className="relative">
      <label htmlFor={id} className="block text-supporting font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-error-red">*</span>}
      </label>
      <input
        type="text"
        id={id}
        value={inputValue}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => setShowSuggestions(true)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        placeholder={placeholder}
        className="w-full px-4 py-3 border border-border-light rounded-md text-body-lg focus:ring-2 focus:ring-trust-blue/20 focus:border-trust-blue transition-all duration-200 font-inter"
        required={required}
      />

      {showSuggestions && filteredOptions.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-pure-white border border-border-light rounded-md shadow-card max-h-64 overflow-y-auto">
          {filteredOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => handleSuggestionClick(option)}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors duration-200"
            >
              <div className="text-body font-medium text-gray-900">{option.name}</div>
              <div className="text-supporting text-text-secondary">{option.address}</div>
              <div className="text-supporting text-trust-blue">{option.phone}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}